import { useMemo, useState } from 'react';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText, lt } from '@mews/framework';
import { WithGuideLink } from '@mews-commander/types';
import { useEnterpriseNavigate } from '@mews-commander/ui';
import { ProductCheckListTable } from '../containers/ProductCheckListTable/ProductCheckListTable';
import { ProductCheckListFiltersContextProvider } from '../contexts/ProductCheckListFiltersContext';
import { ProductCheckListStatistics } from '../containers/ProductCheckListStatistics/ProductCheckListStatistics';
import { useProductsByServiceType } from '../components/panels/ProductCheckListFilterFields/utils/useProductsByServiceType';
import { ServicesProductsContextProvider } from '../contexts/ServicesProductsContext';
import { ProductCheckListDataTestIds } from '../constants';
import { FilterSidebarLayoutBase, FilterSidebarLayoutContent } from '../../../components/layout';
import {
    ProductCheckListFilterValues,
    productCheckListFiltersQuerySchema,
    productCheckListFiltersSchema,
} from '../components/panels/ProductCheckListFilterFields/types';
import { useProductCheckListFiltersDefaultValue } from '../utils/useProductCheckListFiltersDefaultValue';
import { ProductCheckListFilterFields } from '../components/panels/ProductCheckListFilterFields/ProductCheckListFilterFields';
import { ProductCheckListFilterActions } from '../containers/ProductCheckListFilterActions';

interface ProductCheckListPageProps extends WithGuideLink {
    searchParams: Omit<ProductCheckListFilterValues, 'date'>;
}

export const ProductCheckListPage: React.FC<ProductCheckListPageProps> = ({ guideLink, searchParams }) => {
    const [tableIsLoading, setTableIsLoading] = useState(false);
    const { productsByServiceType, productCategories, loading: productsLoading } = useProductsByServiceType();
    const navigate = useEnterpriseNavigate();

    const servicesProductsContextValue = useMemo(
        () => ({ productsByServiceType, productCategories }),
        [productCategories, productsByServiceType]
    );

    const filtersDefaultValue = useProductCheckListFiltersDefaultValue(productsByServiceType);

    return (
        <ServicesProductsContextProvider value={servicesProductsContextValue}>
            <FilterSidebarLayoutBase
                loading={productsLoading || tableIsLoading}
                title={
                    <LocalizedText
                        data-test-id={ProductCheckListDataTestIds.title}
                        textKey={TextKey.ProductCheckList}
                    />
                }
                guideLink={guideLink}
            >
                {(sidebarToggleProps) =>
                    !productsLoading && (
                        <FilterSidebarLayoutContent
                            {...sidebarToggleProps}
                            // @ts-expect-error
                            filterSchema={productCheckListFiltersSchema}
                            defaultFilterValues={filtersDefaultValue}
                            // @ts-expect-error
                            queryParamsSchema={productCheckListFiltersQuerySchema}
                            filterSidebar={{
                                title: lt(TextKey.Filter),
                                fields: <ProductCheckListFilterFields />,
                                actions: (
                                    <ProductCheckListFilterActions
                                        searchParams={searchParams}
                                        filtersDefaultValue={filtersDefaultValue}
                                    />
                                ),
                            }}
                            onFilterSubmit={(v) => {
                                const { date: newDate, ...newFilters } = v;
                                void navigate({ to: 'ProductCheckList/Index', search: newFilters, replace: true });
                            }}
                        >
                            {({ filterValues }) => (
                                <ProductCheckListFiltersContextProvider filters={filterValues}>
                                    <ProductCheckListStatistics />
                                    <ProductCheckListTable onLoadingChange={setTableIsLoading} />
                                </ProductCheckListFiltersContextProvider>
                            )}
                        </FilterSidebarLayoutContent>
                    )
                }
            </FilterSidebarLayoutBase>
        </ServicesProductsContextProvider>
    );
};
